import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Container,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import * as pwApi from '@/api/passwordReset';

const Schema = z
  .object({
    password: z.string().min(12, 'At least 12 characters'),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, { path: ['confirm'], message: 'Passwords do not match' });

type Input = z.infer<typeof Schema>;

export function PasswordResetConfirmPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const code = params.get('code') ?? '';
  const [serverError, setServerError] = useState<string | null>(null);
  const [hints, setHints] = useState<string[] | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Input>({ resolver: zodResolver(Schema) });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    setHints(null);
    try {
      await pwApi.confirmReset(code, values.password);
      navigate('/auth/login', { replace: true });
    } catch (e) {
      if (e instanceof ConsoleApiError) {
        setServerError(e.message);
        setHints(e.body?.hints ?? null);
      } else {
        setServerError('Could not reset password.');
      }
    }
  });

  if (!code) {
    return (
      <Container maxWidth="xs" sx={{ pt: 8 }}>
        <Alert severity="warning">Missing reset code.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xs" sx={{ pt: 8 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Set a new password
      </Typography>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <Stack spacing={2}>
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
            {...register('password')}
          />
          <TextField
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            error={Boolean(errors.confirm)}
            helperText={errors.confirm?.message}
            {...register('confirm')}
          />
          {serverError && (
            <Alert severity="error">
              {serverError}
              {hints && (
                <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                  {hints.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </Box>
              )}
            </Alert>
          )}
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save password'}
          </Button>
          <MuiLink component={RouterLink} to="/auth/login" underline="hover">
            Back to sign-in
          </MuiLink>
        </Stack>
      </Box>
    </Container>
  );
}
